import { useState, useEffect, useCallback, useMemo } from "react";
import { timeFormat } from "../components/shared/constants";
import moment from "moment";

export const useDateTimeRange = ({
    clearable = false,
    startDate: propStartDate,
    endDate: propEndDate,
    startTime: propStartTime,
    endTime: propEndTime,
}) => {
    const [state, setState] = useState({
        startDate: propStartDate,
        endDate: propEndDate,
        startTime: propStartTime,
        endTime: propEndTime,
    });

    const adjustStartDate = useCallback((prevStartDate, newEndDate) => {
        return prevStartDate && newEndDate && prevStartDate.valueOf() > newEndDate.valueOf() ? newEndDate : prevStartDate;
    }, []);

    const adjustEndDate = useCallback((newStartDate, prevEndDate) => {
        return newStartDate && prevEndDate && newStartDate.valueOf() > prevEndDate.valueOf() ? newStartDate : prevEndDate;
    }, []);

    const adjustStartTime = useCallback((newStartDate, newEndDate, prevStartTime, newEndTime) => {
        return newStartDate && newEndDate && prevStartTime && newEndTime
            && (newStartDate.valueOf() === newEndDate.valueOf() && prevStartTime.valueOf() > newEndTime.valueOf())
            ? newEndTime
            : prevStartTime;
    }, []);

    const adjustEndTime = useCallback((newStartDate, newEndDate, newStartTime, prevEndTime) => {
        return newStartDate && newEndDate && newStartTime && prevEndTime
            && (newStartDate.valueOf() === newEndDate.valueOf() && newStartTime.valueOf() > prevEndTime.valueOf())
            ? newStartTime
            : prevEndTime;
    }, []);

    const reset = useCallback(() => {
        setState(() => {
            const defaultDateTime = moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            let newStartDate = propStartDate ? moment(propStartDate).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }) : undefined;
            let newEndDate = propEndDate ? moment(propEndDate).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }) : undefined;
            let newStartTime = propStartTime ? moment(propStartTime, timeFormat) : undefined;
            let newEndTime = propEndTime ? moment(propEndTime, timeFormat) : undefined;

            if (clearable) {
                if (newStartDate?.isValid() && newEndDate?.isValid()) {
                    newEndDate = adjustEndDate(newStartDate, newEndDate);
                } else {
                    newStartDate = newStartDate?.isValid() ? newStartDate : undefined;
                    newEndDate = newEndDate?.isValid() ? newEndDate : undefined;
                }

                newStartTime = newStartDate && newStartTime?.isValid() ? newStartTime : undefined;
                newEndTime = newEndDate && newEndDate?.isValid() ? newEndTime : undefined;

                if (newStartTime?.isValid() && newEndTime?.isValid()) {
                    newEndTime = adjustEndTime(newStartDate, newEndDate, newStartDate, newEndTime);
                }
            } else {
                if (newStartDate?.isValid() && newEndDate?.isValid()) {
                    newEndDate = adjustEndDate(newStartDate, newEndDate);
                } else if (newStartDate?.isValid()) {
                    newEndDate = adjustEndDate(newStartDate, defaultDateTime);
                } else if (newEndDate?.isValid()) {
                    newStartDate = adjustStartDate(defaultDateTime, newEndDate);
                } else {
                    newStartDate = defaultDateTime;
                    newEndDate = defaultDateTime;
                }

                if (newStartTime?.isValid() && newEndTime?.isValid()) {
                    newEndTime = adjustEndTime(newStartDate, newEndDate, newStartDate, newEndTime);
                } else if (newStartTime?.isValid()) {
                    newEndDate = adjustEndTime(newStartDate, newEndDate, newStartTime, defaultDateTime);
                } else if (newEndDate?.isValid()) {
                    newStartDate = adjustStartTime(newStartDate, newEndDate, defaultDateTime, newEndTime);
                } else {
                    newStartTime = defaultDateTime;
                    newEndTime = defaultDateTime;
                }
            }

            return {
                startDate: newStartDate,
                endDate: newEndDate,
                startTime: newStartTime,
                endTime: newEndTime,
            };
        });
    }, [adjustEndDate, adjustEndTime, adjustStartDate, adjustStartTime, clearable, propEndDate, propEndTime, propStartDate, propStartTime]);

    useEffect(() => {
        reset();
    }, [reset]);

    const setNewStartDate = useCallback((value) => {
        setState((prevState) => {
            const defaultDateTime = moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            const momentValue = value ? moment(value) : undefined;
            const newStartDate = momentValue?.isValid() ? momentValue : !clearable ? defaultDateTime : undefined;
            const newStartTime = prevState.startDate && !newStartDate ? undefined : prevState.startTime;
            const newEndDate = adjustEndDate(newStartDate, prevState.endDate);
            const newEndTime = adjustEndTime(newStartDate, newEndDate, newStartTime, prevState.endTime);
            return {
                startDate: newStartDate,
                endDate: newEndDate,
                startTime: newStartTime,
                endTime: newEndTime,
            };
        });
    }, [adjustEndDate, adjustEndTime, clearable]);

    const setNewEndDate = useCallback((value) => {
        setState((prevState) => {
            const defaultDateTime = moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            const momentValue = value ? moment(value) : undefined;
            const newEndDate = momentValue?.isValid() ? momentValue : !clearable ? defaultDateTime : undefined;
            const newEndTime = prevState.endDate && !newEndDate ? undefined : prevState.endTime;
            const newStartDate = adjustStartDate(prevState.startDate, newEndDate);
            const newStartTime = adjustStartTime(newStartDate, newEndDate, prevState.startTime, newEndTime);
            return {
                startDate: newStartDate,
                endDate: newEndDate,
                startTime: newStartTime,
                endTime: newEndTime,
            };
        });
    }, [adjustStartDate, adjustStartTime, clearable]);

    const setNewStartTime = useCallback((value) => {
        setState((prevState) => {
            const momentValue = moment(value, timeFormat);
            const newStartTime = momentValue.isValid() ? momentValue : !clearable ? prevState.startTime : undefined;
            const newEndTime = adjustEndTime(prevState.startDate, prevState.endDate, newStartTime, prevState.endTime);
            return {
                ...prevState,
                startTime: newStartTime,
                endTime: newEndTime,
            };
        });
    }, [adjustEndTime, clearable]);

    const setNewEndTime = useCallback((value) => {
        setState((prevState) => {
            const momentValue = moment(value, timeFormat);
            const newEndTime = momentValue.isValid() ? momentValue : !clearable ? prevState.endTime : undefined;
            const newStartTime = adjustStartTime(prevState.startDate, prevState.endDate, prevState.startTime, newEndTime);
            return {
                ...prevState,
                startTime: newStartTime,
                endTime: newEndTime,
            };
        });
    }, [adjustStartTime, clearable]);

    const memoisedResult = useMemo(() => ({
        startDate: state.startDate?.toDate(),
        endDate: state.endDate?.toDate(),
        startTime: state.startTime?.format(timeFormat),
        endTime: state.endTime?.format(timeFormat),
        setStartDate: setNewStartDate,
        setEndDate: setNewEndDate,
        setStartTime: setNewStartTime,
        setEndTime: setNewEndTime,
        reset,
    }), [reset, setNewEndDate, setNewEndTime, setNewStartDate, setNewStartTime, state.endDate, state.endTime, state.startDate, state.startTime]);

    return memoisedResult;
};
